import React from 'react';
import {useQuery} from '@apollo/client';
import {Button, Drawer, Empty, Image, Skeleton, Tag, Timeline} from 'antd';
import {CloseOutlined} from '@ant-design/icons';
import {useTranslation} from 'react-i18next';
import dayjs from 'dayjs';
import {AppealTicketStatus} from '../graphql/queries/getAppealTicketPageList';
import {
    GET_APPEAL_TICKET_DETAIL,
    GetAppealTicketDetailResponse,
} from '../graphql/queries/getAppealTicketDetail';
import OrderDetailDrawer from './OrderDetailDrawer';
import type {TFunction} from 'i18next';

function renderOperationLogDesc(description: string | null | undefined, t: TFunction): string {
    if (!description) return '';
    const sep = description.indexOf('::');
    if (sep === -1) return t(description, { defaultValue: description });
    const key = description.substring(0, sep);
    const param = description.substring(sep + 2);
    return t(key, { reason: param, defaultValue: param });
}

interface PenaltyDetailDrawerProps {
    ticketId: number | null;
    onClose: () => void;
    onApprove: (id: number) => void;
    onReject: (id: number) => void;
    /** 标记待确认，仅「审核中」的罚单可用 */
    onWaiting: (id: number) => void;
    /** 批量审核模式：标题改为显示「批量审核 (X/Y)」计数，footer 常驻「跳过」按钮 */
    isBatchMode?: boolean;
    /** 批量模式下，当前在批次中的下标（0-based） */
    currentIndex?: number;
    /** 批量模式下，批次总数 */
    total?: number;
    /** 批量模式下的「跳过」：不调用任何审核 mutation，仅推进到下一条 */
    onSkip?: () => void;
}

// 浅底深字的圆角胶囊样式（设计图里的状态标签），不是 AntD Tag 默认的实色矩形，
// 所以不用 Tag 的 color prop，改用 className 自定义覆盖
const STATUS_TAG_CLASS_NAME: Record<AppealTicketStatus, string> = {
    [AppealTicketStatus.PENDING]: '!bg-blue-50 !text-blue-600 !border-0 !rounded-full',
    [AppealTicketStatus.WAITING]: '!bg-orange-50 !text-orange-600 !border-0 !rounded-full',
    [AppealTicketStatus.APPROVED]: '!bg-green-50 !text-green-600 !border-0 !rounded-full',
    [AppealTicketStatus.REJECTED]: '!bg-red-50 !text-red-600 !border-0 !rounded-full',
    [AppealTicketStatus.TIMEOUT]: '!bg-gray-100 !text-gray-600 !border-0 !rounded-full',
};

/**
 * 本页日期统一用 “-” 分隔（不复用全局 formatEpochToDateTime，那个是 “/” 分隔且被大量其它模块共用）。
 *
 * 时间字段均为 api-server 透传的 ISO-8601 字符串（`Instant.toString()`），dayjs 可直接解析，
 * 不需要也不能再做 Number() 转换。
 */
const formatDateTime = (isoString?: string | null): string => {
    if (isoString == null) return '-';
    return dayjs(isoString).format('YYYY-MM-DD HH:mm');
};

const Field: React.FC<{label: string; value: React.ReactNode; valueClassName?: string}> = ({
    label,
    value,
    valueClassName,
}) => (
    <div>
        <div className="text-xs text-gray-500 mb-1">{label}</div>
        <div className={valueClassName ?? 'text-sm text-gray-900'}>{value ?? '--'}</div>
    </div>
);

const SectionTitle: React.FC<{title: string}> = ({title}) => (
    <div className="text-base font-bold mb-3">{title}</div>
);

const PenaltyDetailDrawer: React.FC<PenaltyDetailDrawerProps> = ({
    ticketId,
    onClose,
    onApprove,
    onReject,
    onWaiting,
    isBatchMode = false,
    currentIndex = 0,
    total = 0,
    onSkip,
}) => {
    const {t} = useTranslation();
    const [viewingOrderId, setViewingOrderId] = React.useState<string | null>(null);

    const {data, loading} = useQuery<GetAppealTicketDetailResponse>(GET_APPEAL_TICKET_DETAIL, {
        variables: {id: ticketId},
        skip: ticketId == null,
        fetchPolicy: 'network-only',
    });

    const detail = data?.getAppealTicketDetail;
    // 「审核中」与「待确认」的罚单都可以标记通过/驳回
    const canReview = detail?.status === AppealTicketStatus.PENDING || detail?.status === AppealTicketStatus.WAITING;
    // 状态文案按原始状态编码取三语译文（复用罚单管理中心同一枚举的 key），不展示后端返回的中文 statusDescription；
    // 编码为空或无对应译文（如 ACCEPTED）时回退为申诉审核 Tab 文案
    const tabStatusText = detail ? String(t(`partnerControl.appealReview.tab.${detail.status.toLowerCase()}`)) : '';
    const statusText = detail?.penaltyStatusCode
        ? String(t(`partnerControl.penaltyCenter.status.${detail.penaltyStatusCode}`, {defaultValue: tabStatusText}))
        : tabStatusText;

    return (
        <>
        <Drawer
            open={ticketId != null}
            onClose={onClose}
            width="calc(100vw - 304px)"
            closable={false}
            // body 铺浅灰底，内容按区块分卡片（见下方 4 个 bg-white 容器），让 4 个信息区域在视觉上真正分开，
            // 不只是靠标题文字和留白区分
            styles={{body: {padding: 0, backgroundColor: '#f5f5f5'}}}
            title={
                <div className="flex items-center gap-3">
                    <CloseOutlined className="cursor-pointer hover:text-gray-600" onClick={onClose} />
                    {isBatchMode ? (
                        <span className="text-lg font-medium">
                            {t('partnerControl.appealReview.batchReviewTitle', {
                                current: currentIndex + 1,
                                total,
                            })}
                        </span>
                    ) : (
                        <>
                            <span className="text-lg font-medium">{t('partnerControl.appealReview.detail.title')}</span>
                            {detail && (
                                <>
                                    <Tag>{detail.ticketNo}</Tag>
                                    <Tag className={STATUS_TAG_CLASS_NAME[detail.status]}>
                                        {statusText}
                                    </Tag>
                                </>
                            )}
                        </>
                    )}
                </div>
            }
            extra={
                isBatchMode || canReview ? (
                    <div className="flex items-center gap-2">
                        {detail && canReview && (
                            <>
                                <Button danger onClick={() => onReject(detail.id)}>
                                    {t('partnerControl.appealReview.action.reject')}
                                </Button>
                                <Button
                                    type="primary"
                                    className="!bg-green-600 hover:!bg-green-500"
                                    onClick={() => onApprove(detail.id)}
                                >
                                    {t('partnerControl.appealReview.action.approve')}
                                </Button>
                                {/* 待确认只能从审核中进入，已是待确认的罚单不显示 */}
                                {detail.status === AppealTicketStatus.PENDING && (
                                    <Button onClick={() => onWaiting(detail.id)}>
                                        {t('partnerControl.appealReview.action.waiting')}
                                    </Button>
                                )}
                            </>
                        )}
                        {/* 批量项目可能在审核过程中已被其他运营处理掉（并发场景）：
                            此时罚单已不可审核，通过/驳回按钮不渲染，
                            跳过按钮必须独立于 status 判断渲染，否则用户会卡在这条无法继续 */}
                        {isBatchMode && (
                            <Button onClick={onSkip}>
                                {t('partnerControl.appealReview.action.skip')}
                            </Button>
                        )}
                    </div>
                ) : null
            }
        >
            {loading || !detail ? (
                <div className="p-6">
                    <Skeleton active paragraph={{rows: 8}} />
                </div>
            ) : (
                <div className="flex flex-col gap-4 p-6">
                    <div className="bg-white rounded-lg p-6">
                        <SectionTitle title={t('partnerControl.appealReview.detail.basicInfo')} />
                        <div className="grid grid-cols-3 gap-x-8 gap-y-4">
                            <Field label={t('partnerControl.appealReview.table.ticketNo')} value={detail.ticketNo} />
                            <Field
                                label={t('partnerControl.appealReview.table.trackingNumber')}
                                value={
                                    detail.orderId != null ? (
                                        <Button
                                            type="link"
                                            size="small"
                                            className="!px-0 !h-auto"
                                            onClick={() => setViewingOrderId(detail.orderId as string)}
                                        >
                                            {detail.trackingNumber}
                                        </Button>
                                    ) : (
                                        detail.trackingNumber
                                    )
                                }
                            />
                            <Field
                                label={t('partnerControl.appealReview.table.status')}
                                value={
                                    <Tag className={STATUS_TAG_CLASS_NAME[detail.status]}>
                                        {statusText}
                                    </Tag>
                                }
                            />
                            <Field
                                label={t('partnerControl.appealReview.detail.creationTime')}
                                value={formatDateTime(detail.creationTime)}
                            />
                            <Field label={t('partnerControl.appealReview.table.reason')} value={detail.reason} />
                            <Field label={t('partnerControl.appealReview.table.source')} value={detail.source} />
                            <Field
                                label={t('partnerControl.appealReview.table.amount')}
                                value={detail.amount != null ? `$${detail.amount.toLocaleString()}` : undefined}
                            />
                            <Field
                                label={t('partnerControl.appealReview.detail.driver')}
                                value={detail.driverName}
                            />
                            <Field
                                label={t('partnerControl.appealReview.detail.appealDeadline')}
                                value={formatDateTime(detail.appealDeadline)}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6">
                        <SectionTitle title={t('partnerControl.appealReview.detail.dspInfo')} />
                        <div className="grid grid-cols-4 gap-x-8 gap-y-4">
                            <Field label={t('partnerControl.appealReview.table.dspName')} value={detail.dsp?.name} />
                            <Field label={t('partnerControl.appealReview.detail.dspCode')} value={detail.dsp?.dspCode} />
                            <Field
                                label={t('partnerControl.appealReview.detail.warehouse')}
                                value={detail.warehouseName}
                            />
                            <Field
                                label={t('partnerControl.appealReview.detail.contactPhone')}
                                value={detail.dsp?.contactPhone}
                            />
                        </div>
                    </div>

                    <div className="bg-white rounded-lg p-6">
                        <SectionTitle title={t('partnerControl.appealReview.detail.appealInfo')} />
                        {detail.appealDetails.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('partnerControl.appealReview.detail.noAppeal')} />
                        ) : (
                            // 后端已按申诉次数倒序返回（最新一次在前），前端不再重排
                            <div className="flex flex-col gap-4">
                                {detail.appealDetails.map((appealDetail) => (
                                    <div key={appealDetail.appealCount ?? 0} className="border-b border-gray-100 pb-4 last:border-0 last:pb-0">
                                        <div className="text-xs text-gray-500 mb-2">
                                            {t('partnerControl.appealReview.detail.appellant')}：{detail.driverName ?? '--'}
                                            {'　'}
                                            {t('partnerControl.appealReview.table.appealTime')}：{formatDateTime(appealDetail.appealTime)}
                                        </div>
                                        <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-900 mb-3">
                                            {appealDetail.appealReason || t('partnerControl.appealReview.detail.noAppeal')}
                                        </div>
                                        {appealDetail.evidenceImageUrls.length > 0 && (
                                            // 同一轮申诉的证据图放进一个 PreviewGroup，点开后可左右翻页/键盘切换；
                                            // 按轮次分组而非全部塞一个组，避免未来支持多轮申诉后把不同轮次的证据混在一起翻
                                            <Image.PreviewGroup>
                                                <div className="flex flex-wrap gap-2">
                                                    {appealDetail.evidenceImageUrls.map((url) => (
                                                        <Image key={url} src={url} width={96} height={96} className="rounded object-cover" />
                                                    ))}
                                                </div>
                                            </Image.PreviewGroup>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="bg-white rounded-lg p-6">
                        <SectionTitle title={t('partnerControl.appealReview.detail.operationLogs')} />
                        {detail.operationLogs.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <Timeline
                                items={detail.operationLogs.map((operationLog) => ({
                                    children: (
                                        <div>
                                            <div className="text-xs text-gray-500">
                                                {formatDateTime(operationLog.operatedAt)}
                                                {'　'}
                                                {operationLog.operatorName ??
                                                    t('partnerControl.appealReview.detail.systemOperator')}
                                            </div>
                                            <div className="text-sm text-gray-900">
                                                {renderOperationLogDesc(operationLog.description, t)}
                                            </div>
                                        </div>
                                    ),
                                }))}
                            />
                        )}
                    </div>
                </div>
            )}
        </Drawer>
        <OrderDetailDrawer orderId={viewingOrderId} onClose={() => setViewingOrderId(null)} />
        </>
    );
};

export default PenaltyDetailDrawer;
